import type { Dispatch, KeyboardEvent, SetStateAction } from "react";
import type { CompanyEmployeeSearchOption } from "@/hooks/useCompanyEmployeeSearch";

type EmployeeSearchBoxProps = {
  query: string;
  setQuery: Dispatch<SetStateAction<string>>;
  options: CompanyEmployeeSearchOption[];
  loading?: boolean;
  error?: string | null;
  label?: string;
  placeholder?: string;
  submittedQuery?: string;
  hasActiveQuery?: boolean;
  resultCount?: number;
  onSubmit?: () => void;
  onClear?: () => void;
  compact?: boolean;
};

export default function EmployeeSearchBox({
  query,
  setQuery,
  options,
  loading = false,
  error = null,
  label = "Employee search",
  placeholder = "Search employee",
  submittedQuery = "",
  hasActiveQuery = false,
  resultCount = options.length,
  onSubmit,
  onClear,
  compact = false,
}: EmployeeSearchBoxProps) {
  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    onSubmit?.();
  }

  return (
    <div
      className={
        compact
          ? "flex w-fit flex-col gap-1"
          : "flex min-w-[18rem] flex-col gap-2"
      }
    >
      {!compact ? (
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
          {label}
        </label>
      ) : null}

      <div className="flex gap-2">
        <input
          placeholder={placeholder}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          className={
            compact
              ? "w-[14rem] rounded-xl border border-gray-200 px-3 py-2 text-sm transition-all focus:w-[20rem] disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400"
              : "min-w-0 flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400"
          }
        />

        <button
          type="button"
          onClick={onSubmit}
          disabled={loading}
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        >
          Search
        </button>

        {hasActiveQuery ? (
          <button
            type="button"
            onClick={onClear}
            disabled={loading}
            className="rounded-xl border border-gray-200 px-3 py-2 text-sm disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          >
            Clear
          </button>
        ) : null}
      </div>

      {loading ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Loading employees...
        </p>
      ) : null}

      {!loading && submittedQuery.trim() ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {resultCount === 1
            ? "1 employee match"
            : `${resultCount} employee matches`}
        </p>
      ) : null}

      {error ? (
        <p className="text-xs text-red-600 dark:text-red-300">
          {error}
        </p>
      ) : null}
    </div>
  );
}